import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Link,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Work as WorkIcon,
  LocalOffer as CouponIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { styled } from '@mui/material/styles';

const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[10],
  maxWidth: 450,
  width: '100%',
}));

const validationSchema = yup.object({
  phone: yup
    .string()
    .required('Phone number is required')
    .matches(/^[6-9]\d{9}$/, 'Invalid phone number (10 digits)'),
  password: yup
    .string()
    .required('Password is required')
    .min(4, 'Password must be at least 4 characters'),
});

const isCitizenRole = (role) =>
  ['customer', 'user', 'franchisee_admin'].includes(role);

const citizenTarget = (role, search) => {
  const redirectParam = new URLSearchParams(search).get('redirect');
  if (role === 'franchisee_admin') {
    return redirectParam?.startsWith('/citizen/')
      ? redirectParam
      : '/citizen/home';
  }
  return redirectParam || '/user/coupons';
};

const UserLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Take an authenticated Citizen or Vyapar account to its relevant portal.
  useEffect(() => {
    if (isAuthenticated && isCitizenRole(user?.role)) {
      navigate(user?.role === 'franchisee_admin' ? '/citizen/home' : '/user/coupons', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const formik = useFormik({
    initialValues: {
      phone: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      try {
        // Explicitly pass 'user' session type for user login
        const result = await login(values.phone, values.password, null, 'user');
        if (result.success) {
          const userRole = result.user?.role || '';
          if (isCitizenRole(userRole)) {
            navigate(citizenTarget(userRole, location.search), { replace: true });
          } else {
            setError('This login supports Citizen users and approved Vyapar accounts only.');
          }
        } else {
          setError(result.error || 'Invalid phone number or password. Please try again.');
        }
      } catch (err) {
        setError('An error occurred. Please try again.');
      } finally {
        setLoading(false);
      }
    },
  });

  // Set page title
  useEffect(() => {
    document.title = 'LOCSY User Portal';
    return () => {
      document.title = 'LOCSY Admin Dashboard';
    };
  }, []);

  // Auto-login when phone and password are in URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const phoneParam = searchParams.get('phone');
    const passwordParam = searchParams.get('password');
    
    // If both phone and password are provided, auto-login
    if (phoneParam && passwordParam) {
      const cleanPhone = phoneParam.replace(/\D/g, '').slice(0, 10);
      if (cleanPhone.length === 10 && passwordParam.length >= 4) {
        setLoading(true);
        // Auto-login with provided credentials - explicitly pass 'user' session type
        login(cleanPhone, passwordParam, null, 'user')
          .then((result) => {
            if (result.success) {
              const userRole = result.user?.role || '';
              if (isCitizenRole(userRole)) {
                navigate(citizenTarget(userRole, location.search), { replace: true });
              } else {
                setError('This login supports Citizen users and approved Vyapar accounts only.');
                setLoading(false);
              }
            } else {
              setError(result.error || 'Auto-login failed. Please try manually.');
              setLoading(false);
            }
          })
          .catch((err) => {
            setError('Auto-login failed. Please try manually.');
            setLoading(false);
          });
        return; // Exit early to prevent manual form fill
      }
    }
    
    // If only phone is provided, pre-fill phone number
    if (phoneParam && !passwordParam) {
      const cleanPhone = phoneParam.replace(/\D/g, '').slice(0, 10);
      if (cleanPhone.length === 10) {
        formik.setFieldValue('phone', cleanPhone);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, navigate, login]);

  return (
    <StyledContainer>
      <StyledPaper elevation={3}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              mb: 2,
            }}
          >
            <WorkIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <CouponIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
          </Box>
          <Typography variant="h4" component="h1" gutterBottom color="primary" fontWeight="bold">
            LOCSY User Portal
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Access Loksy Citizen, Job Alerts & Coupons
          </Typography>
        </Box>

        <Box component="form" onSubmit={formik.handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            id="phone"
            name="phone"
            label="Phone Number"
            value={formik.values.phone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={formik.touched.phone && formik.errors.phone}
            margin="normal"
            autoComplete="tel"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon />
                </InputAdornment>
              ),
            }}
            placeholder="9876543210"
          />

          <TextField
            fullWidth
            id="password"
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={(formik.touched.password && formik.errors.password) || 'Default password: last 4 digits of phone number'}
            margin="normal"
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ textAlign: 'right', mt: 0.5 }}>
            <Link
              component={RouterLink}
              to="/user-login/forgot-password"
              underline="hover"
              variant="body2"
              sx={{ fontWeight: 600 }}
            >
              Forgot Password?
            </Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign In'}
          </Button>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="info.dark" sx={{ mb: 1 }}>
              <strong>New User or Vyapar?</strong>
            </Typography>
            <Typography variant="body2" color="info.dark">
              Citizen users can sign in with their mobile number. Approved Vyapar administrators can also use their existing Vyapar phone number and password to open Loksy Citizen.
            </Typography>
          </Box>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              By signing in, you agree to our{' '}
              <Link component={RouterLink} to="/locsy-terms-and-conditions" underline="hover">
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link component={RouterLink} to="/privacy-policy" underline="hover">
                Privacy Policy
              </Link>
            </Typography>
          </Box>
        </Box>
      </StyledPaper>
    </StyledContainer>
  );
};

export default UserLogin;

